// SettingsPage.tsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../lib/api'
import {
  ChevronLeft,
  Maximize,
  Save,
  RefreshCw,
  Settings as SettingsIcon,
  Globe,
  DollarSign,
  Package,
  FileText,
  Scale,
  Monitor,
  Mail,
  Printer,
  Database,
  Key,
  Info,
  Building,
  Store,
  CreditCard,
  Palette,
  Bell,
  Shield,
  Zap,
  Eye,
  EyeOff,
  Users,
  Truck,
  Coffee,
  BarChart,
  Layers,
  Flag,
  Grid,
  Cloud,
  Clock,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { ErrorBoundary } from '../components/ui/ErrorBoundary'

import { taxCalculator } from '../lib/taxCalculator'
import { barcodeGenerator } from '../lib/barcodeGenerator'

// ============================================
// INTERFACE DEFINITIONS BASED ON SCHEMA
// ============================================

interface ApplicationSetting {
  id: string
  setting_key: string
  setting_value: string | null
  setting_type: string | null
  description: string | null
  is_system: boolean
  created_at: string
  updated_at: string
}

interface Company {
  id: string
  code: string
  name: string
  legal_name: string | null
  tax_number: string | null
  vat_number: string | null
  registration_number: string | null
  industry: string | null
  website: string | null
  email: string | null
  phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country_id: string | null
  country?: string | null
  currency_id: string | null
  language_id: string | null
  timezone: string
  logo_url: string | null
  date_format: string
  time_format: string
  fiscal_year_start: number
  receipt_header_text?: string | null
  receipt_footer_text?: string | null
  is_active: boolean
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

interface PrinterConfiguration {
  id: string
  company_id: string
  branch_id: string
  name: string
  printer_type: string
  connection_type: string
  ip_address: string | null
  port: number | null
  usb_vendor_id: string | null
  usb_product_id: string | null
  paper_width: number
  is_default: boolean
  is_active: boolean
  config_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

interface EmailTemplate {
  id: string
  company_id: string
  branch_id: string | null
  name: string
  template_type: string
  subject: string
  from_email: string | null
  from_name: string | null
  reply_to: string | null
  email_body_html: string
  email_body_text: string | null
  trigger_event: string | null
  send_delay_minutes: number
  variables_json: Record<string, unknown> | null
  conditions_json: Record<string, unknown> | null
  is_active: boolean
  is_default: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

interface Store {
  id: string
  company_id: string
  region_id: string | null
  territory_id: string | null
  code: string
  name: string
  branch_type: string
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country_id: string | null
  latitude: string | null
  longitude: string | null
  phone: string | null
  email: string | null
  manager_id: string | null
  currency_id: string | null
  timezone: string
  tax_number: string | null
  vat_number: string | null
  receipt_header: string | null
  receipt_footer: string | null
  logo_url: string | null
  operating_hours_json: Record<string, unknown>
  is_pos_enabled: boolean
  is_ecommerce_enabled: boolean
  is_active: boolean
  sort_order: number
  notes: string | null
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

interface CashRegister {
  id: string
  company_id: string
  branch_id: string
  register_number: string
  name: string
  register_type: string
  is_active: boolean
  is_online: boolean
  current_user_id: string | null
  current_balance: string
  ip_address: string | null
  mac_address: string | null
  hardware_info: Record<string, unknown>
  last_heartbeat: string | null
  created_at: string
  updated_at: string
}

interface TaxRate {
  id: string
  name: string
  code: string
  rate: number
  is_active: boolean
  is_default: boolean
  description: string | null
  created_at: string
  updated_at: string
}

interface PaymentType {
  id: string
  name: string
  code: string
  is_active: boolean
  opens_cash_drawer: boolean
  requires_reference: boolean
  allows_change: boolean
  is_default: boolean
  sort_order: number
  image: string | null
  color: string | null
  created_at: string
  updated_at: string
}

interface TerminalConfiguration {
  id: string
  cash_register_id: string | null
  terminal_type: string | null
  screen_resolution: string | null
  touch_enabled: boolean
  scanner_type: string | null
  printer_default_id: string | null
  kitchen_printer_id: string | null
  payment_gateway_id: string | null
  default_layout_id: string | null
  auto_logout_minutes: number | null
  require_pin_on_void: boolean
  require_pin_on_discount: boolean
  require_pin_on_refund: boolean
  max_discount_without_approval: number | null
  enable_offline_mode: boolean
  sync_interval_minutes: number | null
  locale: string | null
  currency_code: string | null
  hardware_config: any | null
  created_at: string
  updated_at: string
}

type TabType = 'general' | 'company' | 'stores' | 'taxes' | 'payment-types' | 
               'cash-registers' | 'terminal-config' | 'order-payment' | 'products' | 
               'documents' | 'weighing-scale' | 'customer-display' | 'email' | 
               'print' | 'users' | 'notifications' | 'security' | 
               'about' | 'integrations'

export default function SettingsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<TabType>('general')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle')

  // Application Settings
  const [settings, setSettings] = useState<Record<string, string>>({})
  const [originalSettings, setOriginalSettings] = useState<Record<string, string>>({})
  const [allSettings, setAllSettings] = useState<ApplicationSetting[]>([])

  // Company Data
  const [company, setCompany] = useState<Company | null>(null)
  const [originalCompany, setOriginalCompany] = useState<Company | null>(null)

  // Related Data Collections
  const [printers, setPrinters] = useState<PrinterConfiguration[]>([])
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([])
  const [stores, setStores] = useState<Store[]>([])
  const [cashRegisters, setCashRegisters] = useState<CashRegister[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([])
  const [terminalConfigs, setTerminalConfigs] = useState<TerminalConfiguration[]>([])

  // Form States
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('587')
  const [smtpUsername, setSmtpUsername] = useState('')
  const [smtpPassword, setSmtpPassword] = useState('')
  const [smtpEncryption, setSmtpEncryption] = useState('tls')
  const [showSmtpPassword, setShowSmtpPassword] = useState(false)

  // New Store Form — fields match branches table in schema
  const [newStore, setNewStore] = useState<Partial<Store>>({
    name: '',
    code: '',
    branch_type: 'retail',
    is_pos_enabled: true,
    is_ecommerce_enabled: false,
    is_active: true,
    sort_order: 0
  })

  // New Cash Register Form — fields match cash_registers table in schema
  const [newCashRegister, setNewCashRegister] = useState<Partial<CashRegister>>({
    name: '',
    register_number: '',
    branch_id: '',
    register_type: 'standard',
    is_active: true,
    is_online: false,
    hardware_info: {}
  })

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        fetchSettings(),
        fetchCompany(),
        fetchPrinters(),
        fetchEmailTemplates(),
        fetchStores(),
        fetchCashRegisters(),
        fetchTaxRates(),
        fetchPaymentTypes(),
        fetchTerminalConfigs()
      ])
    } catch (err: any) {
      toast.error('Failed to load settings')
      console.error('Fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/api/settings')
      const settingsList = response.data as ApplicationSetting[]
      setAllSettings(settingsList)

      const settingsMap: Record<string, string> = {}
      settingsList.forEach((setting) => {
        if (setting.setting_value !== null) {
          settingsMap[setting.setting_key] = setting.setting_value
        }
      })
      setSettings(settingsMap)
      setOriginalSettings(settingsMap)

      // Load SMTP settings
      setSmtpHost(settingsMap.smtp_host || '')
      setSmtpPort(settingsMap.smtp_port || '587')
      setSmtpUsername(settingsMap.smtp_username || '')
      setSmtpPassword(settingsMap.smtp_password || '')
      setSmtpEncryption(settingsMap.smtp_encryption || 'tls')
    } catch (err: any) {
      console.error('Failed to fetch settings:', err)
      throw err
    }
  }

  const fetchCompany = async () => {
    try {
      const response = await apiClient.get('/api/company')
      const companyData = response.data[0] || response.data
      setCompany(companyData)
      setOriginalCompany(companyData)
    } catch (err: any) {
      console.error('Failed to fetch company:', err)
    }
  }

  const fetchPrinters = async () => {
    try {
      const response = await apiClient.get('/api/printers/configurations')
      setPrinters(response.data)
    } catch (err: any) {
      console.error('Failed to fetch printers:', err)
    }
  }

  const fetchEmailTemplates = async () => {
    try {
      const response = await apiClient.get('/api/email/templates')
      setEmailTemplates(response.data)
    } catch (err: any) {
      console.error('Failed to fetch email templates:', err)
    }
  }

  const fetchStores = async () => {
    try {
      const response = await apiClient.get('/api/stores')
      setStores(response.data)
    } catch (err: any) {
      console.error('Failed to fetch stores:', err)
    }
  }

  const fetchCashRegisters = async () => {
    try {
      const response = await apiClient.get('/api/cash-registers')
      setCashRegisters(response.data)
    } catch (err: any) {
      console.error('Failed to fetch cash registers:', err)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await apiClient.get('/api/tax-rates')
      setTaxRates(response.data)
    } catch (err: any) {
      console.error('Failed to fetch tax rates:', err)
    }
  }

  const fetchPaymentTypes = async () => {
    try {
      const response = await apiClient.get('/api/payment-types')
      setPaymentTypes(response.data)
    } catch (err: any) {
      console.error('Failed to fetch payment types:', err)
    }
  }

  const fetchTerminalConfigs = async () => {
    try {
      const response = await apiClient.get('/api/terminal-configurations')
      setTerminalConfigs(response.data)
    } catch (err: any) {
      console.error('Failed to fetch terminal configs:', err)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const changedSettings = Object.entries(settings).filter(
        ([key, value]) => originalSettings[key] !== value
      ).map(([key, value]) => ({ setting_key: key, setting_value: value }))

      // Save application settings
      if (changedSettings.length > 0) {
        await apiClient.post('/api/settings/bulk-update', { settings: changedSettings })
      }

      // Save company data if changed
      if (company && JSON.stringify(company) !== JSON.stringify(originalCompany)) {
        await apiClient.put(`/api/company/${company.id}`, company)
        setOriginalCompany(company)
      }

      // Save SMTP settings
      const smtpSettings = [
        { setting_key: 'smtp_host', setting_value: smtpHost },
        { setting_key: 'smtp_port', setting_value: smtpPort },
        { setting_key: 'smtp_username', setting_value: smtpUsername },
        { setting_key: 'smtp_password', setting_value: smtpPassword },
        { setting_key: 'smtp_encryption', setting_value: smtpEncryption }
      ]
      await apiClient.post('/api/settings/bulk-update', { settings: smtpSettings })

      toast.success('Settings saved successfully')
      setOriginalSettings(settings)

      // Refresh data
      await fetchAllData()
    } catch (err: any) {
      toast.error('Failed to save settings')
      console.error('Save error:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setSettings(originalSettings)
    setCompany(originalCompany)
    setSmtpHost(originalSettings.smtp_host || '')
    setSmtpPort(originalSettings.smtp_port || '587')
    setSmtpUsername(originalSettings.smtp_username || '')
    setSmtpPassword(originalSettings.smtp_password || '')
    setSmtpEncryption(originalSettings.smtp_encryption || 'tls')
    toast.success('Settings reset to last saved state')
  }

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value.toString() }))
  }

  const updateCompany = (field: keyof Company, value: any) => {
    if (company) {
      setCompany({ ...company, [field]: value })
    }
  }

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen)
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const testSMTPConnection = async () => {
    setTestStatus('testing')
    try {
      await apiClient.post('/api/email/test', {
        host: smtpHost,
        port: parseInt(smtpPort),
        username: smtpUsername,
        password: smtpPassword,
        encryption: smtpEncryption
      })
      setTestStatus('success')
      toast.success('Test email sent successfully')
    } catch (err) {
      setTestStatus('error')
      toast.error('Failed to send test email')
    } finally {
      setTimeout(() => setTestStatus('idle'), 3000)
    }
  }

  const tabs = [
    { key: 'general' as TabType, label: 'General', icon: SettingsIcon },
    { key: 'company' as TabType, label: 'Company', icon: Building },
    { key: 'stores' as TabType, label: 'Stores', icon: Store },
    { key: 'cash-registers' as TabType, label: 'Cash Registers', icon: CreditCard },
    { key: 'terminal-config' as TabType, label: 'Terminal Config', icon: Grid },
    { key: 'taxes' as TabType, label: 'Taxes', icon: Flag },
    { key: 'payment-types' as TabType, label: 'Payment Types', icon: CreditCard },
    { key: 'order-payment' as TabType, label: 'Order & Payment', icon: BarChart },
    { key: 'products' as TabType, label: 'Products', icon: Package },
    { key: 'documents' as TabType, label: 'Documents', icon: FileText },
    { key: 'weighing-scale' as TabType, label: 'Weighing Scale', icon: Scale },
    { key: 'customer-display' as TabType, label: 'Customer Display', icon: Monitor },
    { key: 'email' as TabType, label: 'Email', icon: Mail },
    { key: 'print' as TabType, label: 'Print', icon: Printer },
    { key: 'users' as TabType, label: 'Users', icon: Users },
    { key: 'notifications' as TabType, label: 'Notifications', icon: Bell },
    { key: 'security' as TabType, label: 'Security', icon: Shield },
    { key: 'integrations' as TabType, label: 'Integrations', icon: Layers },
    { key: 'about' as TabType, label: 'About', icon: Info }
  ]

  // ============================================
  // RENDER FUNCTIONS
  // ============================================

  const renderGeneralSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Globe size={20} className="text-blue-400" />
          Application Style
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Language</label>
              <select 
                value={settings.language || 'en'}
                onChange={(e) => updateSetting('language', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="ar">Arabic</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Color Scheme</label>
              <select 
                value={settings.color_scheme || 'dark'}
                onChange={(e) => updateSetting('color_scheme', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value="dark">Dark</option>
                <option value="light">Light</option>
                <option value="auto">Auto (System)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Timezone</label>
              <select 
                value={settings.timezone || 'UTC'}
                onChange={(e) => updateSetting('timezone', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">New York (EST)</option>
                <option value="America/Chicago">Chicago (CST)</option>
                <option value="America/Denver">Denver (MST)</option>
                <option value="America/Los_Angeles">Los Angeles (PST)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Date Format</label>
              <select 
                value={settings.date_format || 'MM/DD/YYYY'}
                onChange={(e) => updateSetting('date_format', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <DollarSign size={20} className="text-green-400" />
          Currency & Format
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Currency Symbol</label>
              <input
                type="text"
                value={settings.currency_symbol || '$'}
                onChange={(e) => updateSetting('currency_symbol', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="$"
                maxLength={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Currency Code</label>
              <input
                type="text"
                value={settings.currency_code || 'USD'}
                onChange={(e) => updateSetting('currency_code', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="USD"
                maxLength={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Decimal Places</label>
              <input
                type="number"
                min="0"
                max="4"
                value={settings.decimal_places || '2'}
                onChange={(e) => updateSetting('decimal_places', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Thousand Separator</label>
              <select 
                value={settings.thousand_separator || ','}
                onChange={(e) => updateSetting('thousand_separator', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value=",">Comma (,)</option>
                <option value=".">Period (.)</option>
                <option value=" ">Space ( )</option>
                <option value="">None</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Decimal Separator</label>
              <select 
                value={settings.decimal_separator || '.'}
                onChange={(e) => updateSetting('decimal_separator', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value=".">Period (.)</option>
                <option value=",">Comma (,)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Price Rounding</label>
              <select 
                value={settings.price_rounding || 'nearest'}
                onChange={(e) => updateSetting('price_rounding', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value="none">No rounding</option>
                <option value="nearest">Nearest 0.01</option>
                <option value="up">Always up</option>
                <option value="down">Always down</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderCompanySettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Building size={20} className="text-purple-400" />
          Company Information
        </h3>
        {company && (
          <div className="bg-gray-800 rounded-lg p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Company Name *</label>
                <input
                  type="text"
                  value={company.name}
                  onChange={(e) => updateCompany('name', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Address Line 1</label>
                <input
                  type="text"
                  value={company.address_line1 || ''}
                  onChange={(e) => updateCompany('address_line1', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">City</label>
                <input
                  type="text"
                  value={company.city || ''}
                  onChange={(e) => updateCompany('city', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Postal Code</label>
                <input
                  type="text"
                  value={company.postal_code || ''}
                  onChange={(e) => updateCompany('postal_code', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Country</label>
                <input
                  type="text"
                  value={company.country || ''}
                  onChange={(e) => updateCompany('country', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <input
                  type="tel"
                  value={company.phone || ''}
                  onChange={(e) => updateCompany('phone', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={company.email || ''}
                  onChange={(e) => updateCompany('email', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Website</label>
                <input
                  type="url"
                  value={company.website || ''}
                  onChange={(e) => updateCompany('website', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tax Number</label>
                <input
                  type="text"
                  value={company.tax_number || ''}
                  onChange={(e) => updateCompany('tax_number', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Registration Number</label>
                <input
                  type="text"
                  value={company.registration_number || ''}
                  onChange={(e) => updateCompany('registration_number', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">VAT Registration</label>
                <input
                  type="text"
                  value={company.vat_number || ''}
                  onChange={(e) => updateCompany('vat_number', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Fiscal Year Start Month</label>
                <select 
                  value={company.fiscal_year_start || 1}
                  onChange={(e) => updateCompany('fiscal_year_start', parseInt(e.target.value))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Company Logo URL</label>
                <input
                  type="text"
                  value={company.logo_url || ''}
                  onChange={(e) => updateCompany('logo_url', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Receipt Header Text</label>
                <textarea
                  value={company.receipt_header_text || ''}
                  onChange={(e) => updateCompany('receipt_header_text', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  rows={3}
                  placeholder="Welcome to our store!"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Receipt Footer Text</label>
                <textarea
                  value={company.receipt_footer_text || ''}
                  onChange={(e) => updateCompany('receipt_footer_text', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  rows={3}
                  placeholder="Thank you for your business!"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderStoresSettings = () => (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Store size={20} className="text-blue-400" />
          Store Management
        </h3>

        {/* Store List */}
        <div className="bg-gray-800 rounded-lg overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h4 className="text-sm font-semibold">All Stores ({stores.length})</h4>
            <button 
              onClick={() => (document.getElementById('new-store-modal') as any)?.showModal()}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              + Add Store
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {stores.map(store => (
                  <tr key={store.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm font-medium text-white font-mono">{store.code}</td>
                    <td className="px-4 py-3 text-sm text-white">{store.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{store.city || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{store.branch_type || 'retail'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        store.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {store.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button className="text-blue-400 hover:text-blue-300 text-xs">
                          Edit
                        </button>
                        <button className="text-red-400 hover:text-red-300 text-xs">
                          {store.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* New Store Modal */}
        <dialog id="new-store-modal" className="bg-gray-800 rounded-lg shadow-xl max-w-2xl text-white">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Store</h3>
              <button 
                onClick={() => (document.getElementById('new-store-modal') as any)?.close()}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Branch Code *</label>
                  <input
                    type="text"
                    value={newStore.code || ''}
                    onChange={(e) => setNewStore({...newStore, code: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="BR001"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Branch Name *</label>
                  <input
                    type="text"
                    value={newStore.name || ''}
                    onChange={(e) => setNewStore({...newStore, name: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="Main Branch"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Branch Type</label>
                  <select 
                    value={newStore.branch_type || 'retail'}
                    onChange={(e) => setNewStore({...newStore, branch_type: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  >
                    <option value="retail">Retail</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="kiosk">Kiosk</option>
                    <option value="mobile">Mobile</option>
                    <option value="ecommerce">E-Commerce</option>
                    <option value="franchise">Franchise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Timezone</label>
                  <select 
                    value={newStore.timezone || 'UTC'}
                    onChange={(e) => setNewStore({...newStore, timezone: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">New York (EST)</option>
                    <option value="America/Chicago">Chicago (CST)</option>
                    <option value="America/Los_Angeles">Los Angeles (PST)</option>
                    <option value="Europe/London">London (GMT)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Address Line 1</label>
                  <input
                    type="text"
                    value={newStore.address_line1 || ''}
                    onChange={(e) => setNewStore({...newStore, address_line1: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={newStore.city || ''}
                    onChange={(e) => setNewStore({...newStore, city: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="New York"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Postal Code</label>
                  <input
                    type="text"
                    value={newStore.postal_code || ''}
                    onChange={(e) => setNewStore({...newStore, postal_code: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="10001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newStore.phone || ''}
                    onChange={(e) => setNewStore({...newStore, phone: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={newStore.email || ''}
                    onChange={(e) => setNewStore({...newStore, email: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                    placeholder="branch@example.com"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newStore.is_pos_enabled ?? true}
                    onChange={(e) => setNewStore({...newStore, is_pos_enabled: e.target.checked})}
                    className="rounded bg-gray-700 border-gray-600"
                  />
                  <span className="text-sm">POS Enabled</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newStore.is_ecommerce_enabled ?? false}
                    onChange={(e) => setNewStore({...newStore, is_ecommerce_enabled: e.target.checked})}
                    className="rounded bg-gray-700 border-gray-600"
                  />
                  <span className="text-sm">E-Commerce Enabled</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newStore.is_active ?? true}
                    onChange={(e) => setNewStore({...newStore, is_active: e.target.checked})}
                    className="rounded bg-gray-700 border-gray-600"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
                <button 
                  onClick={() => (document.getElementById('new-store-modal') as any)?.close()}
                  className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await apiClient.post('/api/stores', newStore);
                      toast.success('Branch added successfully');
                      await fetchStores();
                      (document.getElementById('new-store-modal') as any)?.close();
                      setNewStore({
                        name: '',
                        code: '',
                        branch_type: 'retail',
                        is_pos_enabled: true,
                        is_ecommerce_enabled: false,
                        is_active: true,
                        sort_order: 0
                      })
                    } catch (err) {
                      toast.error('Failed to add branch')
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save Branch
                </button>
              </div>
            </div>
          </div>
        </dialog>
      </div>
    </div>
  )

  const renderCashRegistersSettings = () => (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <CreditCard size={20} className="text-green-400" />
          Cash Register Management
        </h3>

        <div className="bg-gray-800 rounded-lg overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h4 className="text-sm font-semibold">Registered Terminals ({cashRegisters.length})</h4>
            <button 
              onClick={() => (document.getElementById('new-register-modal') as any)?.showModal()}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              + Add Register
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Register #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Branch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Last Heartbeat</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {cashRegisters.map(register => {
                  const branch = stores.find(s => s.id === register.branch_id)
                  return (
                    <tr key={register.id} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-sm font-medium text-white font-mono">{register.register_number}</td>
                      <td className="px-4 py-3 text-sm text-white">{register.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{branch?.name || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            register.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                          }`}>
                            {register.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {register.last_heartbeat && (
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {register.last_heartbeat ? 
                          new Date(register.last_heartbeat).toLocaleTimeString() : 
                          'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button className="text-blue-400 hover:text-blue-300 text-xs">
                            Edit
                          </button>
                          <button className="text-yellow-400 hover:text-yellow-300 text-xs">
                            Remote Lock
                          </button>
                          <button className="text-red-400 hover:text-red-300 text-xs">
                            {register.is_active ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* New Cash Register Modal */}
      <dialog id="new-register-modal" className="bg-gray-800 rounded-lg shadow-xl max-w-xl text-white">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Add Cash Register</h3>
            <button 
              onClick={() => (document.getElementById('new-register-modal') as any)?.close()}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Register Number *</label>
                <input
                  type="text"
                  value={newCashRegister.register_number || ''}
                  onChange={(e) => setNewCashRegister({...newCashRegister, register_number: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="REG-001"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Register Name *</label>
                <input
                  type="text"
                  value={newCashRegister.name || ''}
                  onChange={(e) => setNewCashRegister({...newCashRegister, name: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="Main Checkout"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Branch</label>
                <select
                  value={newCashRegister.branch_id || ''}
                  onChange={(e) => setNewCashRegister({...newCashRegister, branch_id: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                >
                  <option value="">Select Branch...</option>
                  {stores.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Register Type</label>
                <select
                  value={newCashRegister.register_type || 'standard'}
                  onChange={(e) => setNewCashRegister({...newCashRegister, register_type: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                >
                  <option value="standard">Standard</option>
                  <option value="mobile">Mobile</option>
                  <option value="kiosk">Kiosk</option>
                  <option value="virtual">Virtual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">IP Address</label>
                <input
                  type="text"
                  value={newCashRegister.ip_address || ''}
                  onChange={(e) => setNewCashRegister({...newCashRegister, ip_address: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="192.168.1.100"
                />
              </div>
              <div className="flex items-center gap-4 pt-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newCashRegister.is_active ?? true}
                    onChange={(e) => setNewCashRegister({...newCashRegister, is_active: e.target.checked})}
                    className="rounded bg-gray-700 border-gray-600"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
              <button 
                onClick={() => (document.getElementById('new-register-modal') as any)?.close()}
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await apiClient.post('/api/cash-registers', newCashRegister);
                    toast.success('Cash register added successfully');
                    await fetchCashRegisters();
                    (document.getElementById('new-register-modal') as any)?.close();
                    setNewCashRegister({
                      name: '',
                      register_number: '',
                      branch_id: '',
                      register_type: 'standard',
                      is_active: true,
                      is_online: false,
                      hardware_info: {}
                    })
                  } catch (err) {
                    toast.error('Failed to add cash register')
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Save Register
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  )

  const renderTerminalConfigSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Grid size={20} className="text-purple-400" />
          Terminal Configuration
        </h3>

        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          {terminalConfigs.map(config => (
            <div key={config.id} className="border border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-semibold">Configuration #{config.id.slice(0, 8)}</h4>
                <span className="text-xs text-gray-400">
                  {config.cash_register_id ? 'Bound to register' : 'Global config'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Auto Logout:</span>
                  <span className="ml-2">{config.auto_logout_minutes || 30} min</span>
                </div>
                <div>
                  <span className="text-gray-400">Sync Interval:</span>
                  <span className="ml-2">{config.sync_interval_minutes || 5} min</span>
                </div>
                <div>
                  <span className="text-gray-400">PIN Required:</span>
                  <span className="ml-2">
                    {config.require_pin_on_void ? 'Void' : ''} 
                    {config.require_pin_on_discount ? ' Discount' : ''}
                    {config.require_pin_on_refund ? ' Refund' : ''}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Offline Mode:</span>
                  <span className={`ml-2 ${config.enable_offline_mode ? 'text-green-400' : 'text-gray-400'}`}>
                    {config.enable_offline_mode ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700 flex justify-end">
                <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                  Edit Config
                </button>
              </div>
            </div>
          ))}

          {terminalConfigs.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <Grid size={48} className="mx-auto mb-4 opacity-50" />
              <p>No terminal configurations found</p>
              <p className="text-sm mt-2">Add a cash register to create configurations</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderTaxSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Flag size={20} className="text-red-400" />
          Tax Rates
        </h3>

        <div className="bg-gray-800 rounded-lg overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h4 className="text-sm font-semibold">Tax Rates ({taxRates.length})</h4>
            <button className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
              + Add Tax Rate
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Default</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {taxRates.map(tax => (
                  <tr key={tax.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm font-medium text-white">{tax.code}</td>
                    <td className="px-4 py-3 text-sm text-white">{tax.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{tax.rate}%</td>
                    <td className="px-4 py-3">
                      {tax.is_default && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        tax.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {tax.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button className="text-blue-400 hover:text-blue-300 text-xs">
                          Edit
                        </button>
                        <button className="text-red-400 hover:text-red-300 text-xs">
                          {tax.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPaymentTypesSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <CreditCard size={20} className="text-blue-400" />
          Payment Types
        </h3>

        <div className="bg-gray-800 rounded-lg overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h4 className="text-sm font-semibold">Payment Methods ({paymentTypes.length})</h4>
            <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              + Add Payment Type
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Opens Drawer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Allows Change</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Default</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paymentTypes.map(paymentType => (
                  <tr key={paymentType.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm font-medium text-white">{paymentType.code}</td>
                    <td className="px-4 py-3 text-sm text-white">{paymentType.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        paymentType.opens_cash_drawer ? 'bg-yellow-900 text-yellow-200' : 'bg-gray-800 text-gray-400'
                      }`}>
                        {paymentType.opens_cash_drawer ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        paymentType.allows_change ? 'bg-green-900 text-green-200' : 'bg-gray-800 text-gray-400'
                      }`}>
                        {paymentType.allows_change ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {paymentType.is_default && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        paymentType.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {paymentType.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button className="text-blue-400 hover:text-blue-300 text-xs">
                          Edit
                        </button>
                        <button className="text-red-400 hover:text-red-300 text-xs">
                          {paymentType.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )

  const renderOrderPaymentSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <BarChart size={20} className="text-green-400" />
          Order & Payment Settings
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Auto Print Receipt</div>
              <div className="text-xs text-gray-400">Automatically print receipt after payment</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.auto_print_receipt === 'true'}
                onChange={(e) => updateSetting('auto_print_receipt', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Require Customer on Sale</div>
              <div className="text-xs text-gray-400">Force customer selection for all transactions</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.require_customer_on_sale === 'true'}
                onChange={(e) => updateSetting('require_customer_on_sale', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Allow Negative Stock</div>
              <div className="text-xs text-gray-400">Allow sales when inventory is out of stock</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.allow_negative_stock === 'true'}
                onChange={(e) => updateSetting('allow_negative_stock', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Hold Order Timeout (minutes)</div>
              <div className="text-xs text-gray-400">Time before held orders are auto-canceled</div>
            </div>
            <input
              type="number"
              min="1"
              max="240"
              value={settings.hold_order_timeout || '30'}
              onChange={(e) => updateSetting('hold_order_timeout', e.target.value)}
              className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-1 text-sm text-right"
            />
          </div>
        </div>
      </div>
    </div>
  )

  const renderProductSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package size={20} className="text-green-400" />
          Product Settings
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Tax Inclusive Pricing</div>
              <div className="text-xs text-gray-400">Display prices including tax</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.tax_inclusive_pricing === 'true'}
                onChange={(e) => updateSetting('tax_inclusive_pricing', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Barcode Prefix for Weighing Items</label>
            <input
              type="text"
              value={settings.weighed_barcode_prefix || '2'}
              onChange={(e) => updateSetting('weighed_barcode_prefix', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              placeholder="2"
              maxLength={5}
            />
            <div className="text-xs text-gray-400 mt-1">
              Barcodes starting with this prefix will trigger scale reading (usually "2" for PLU codes)
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Default Category Sort Order</label>
            <select 
              value={settings.default_category_sort || 'name'}
              onChange={(e) => updateSetting('default_category_sort', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="name">Name (A-Z)</option>
              <option value="custom">Custom Order</option>
              <option value="created">Recently Added</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>

          <div className="flex items-center justify-between py-3 border-t border-gray-700">
            <div>
              <div className="text-sm font-medium">Track Inventory</div>
              <div className="text-xs text-gray-400">Enable inventory tracking for all products</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.track_inventory === 'true'}
                onChange={(e) => updateSetting('track_inventory', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderDocumentsSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText size={20} className="text-blue-400" />
          Document Templates
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Default Receipt Template</label>
            <select 
              value={settings.default_receipt_template || 'thermal-80mm'}
              onChange={(e) => updateSetting('default_receipt_template', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="thermal-80mm">Thermal (80mm)</option>
              <option value="thermal-58mm">Thermal (58mm)</option>
              <option value="a4-invoice">A4 Invoice</option>
              <option value="pos-receipt">POS Receipt</option>
              <option value="custom">Custom Template</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Default Invoice Template</label>
            <select 
              value={settings.default_invoice_template || 'a4-invoice'}
              onChange={(e) => updateSetting('default_invoice_template', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="a4-invoice">A4 Detailed Invoice</option>
              <option value="a4-simple">A4 Simple Invoice</option>
              <option value="thermal-invoice">Thermal Invoice</option>
              <option value="pos-invoice">POS Invoice</option>
            </select>
          </div>

          <div className="flex items-center justify-between py-3 border-t border-gray-700">
            <div>
              <div className="text-sm font-medium">Include Company Logo on Documents</div>
              <div className="text-xs text-gray-400">Show logo on printed receipts and invoices</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.include_company_logo === 'true'}
                onChange={(e) => updateSetting('include_company_logo', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Include Tax Breakdown</div>
              <div className="text-xs text-gray-400">Show detailed tax calculations on receipts</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.include_tax_breakdown === 'true'}
                onChange={(e) => updateSetting('include_tax_breakdown', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Document Footer Text</label>
            <textarea
              value={settings.document_footer || ''}
              onChange={(e) => updateSetting('document_footer', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              rows={3}
              placeholder="Additional information to display on documents"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Receipt Copies</label>
            <input
              type="number"
              min="1"
              max="5"
              value={settings.receipt_copies || '1'}
              onChange={(e) => updateSetting('receipt_copies', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  )

  const renderWeighingScaleSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Scale size={20} className="text-green-400" />
          Weighing Scale Configuration
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Scale Type</label>
            <select 
              value={settings.scale_type || 'none'}
              onChange={(e) => updateSetting('scale_type', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="none">No Scale Connected</option>
              <option value="serial">Serial Port Scale</option>
              <option value="usb">USB Scale</option>
              <option value="network">Network Scale</option>
              <option value="bluetooth">Bluetooth Scale</option>
            </select>
          </div>

          {settings.scale_type !== 'none' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Connection Port/Address</label>
                <input
                  type="text"
                  value={settings.scale_port || ''}
                  onChange={(e) => updateSetting('scale_port', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="e.g., COM3, /dev/ttyUSB0, 192.168.1.100"
                />
              </div>

              {settings.scale_type === 'serial' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Baud Rate</label>
                  <select 
                    value={settings.scale_baud_rate || '9600'}
                    onChange={(e) => updateSetting('scale_baud_rate', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  >
                    <option value="2400">2400</option>
                    <option value="4800">4800</option>
                    <option value="9600">9600</option>
                    <option value="19200">19200</option>
                    <option value="38400">38400</option>
                    <option value="57600">57600</option>
                    <option value="115200">115200</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Unit of Measure</label>
                <select 
                  value={settings.scale_unit || 'kg'}
                  onChange={(e) => updateSetting('scale_unit', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                >
                  <option value="kg">Kilograms (kg)</option>
                  <option value="g">Grams (g)</option>
                  <option value="lb">Pounds (lb)</option>
                  <option value="oz">Ounces (oz)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Barcode Prefix for Weighed Items</label>
                <input
                  type="text"
                  value={settings.weighed_barcode_prefix || '2'}
                  onChange={(e) => updateSetting('weighed_barcode_prefix', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="2"
                  maxLength={5}
                />
                <div className="text-xs text-gray-400 mt-1">
                  Barcodes starting with this prefix will trigger scale reading (usually "2" for PLU codes)
                </div>
              </div>

              <div className="pt-4 border-t border-gray-700">
                <button
                  onClick={async () => {
                    try {
                      toast.success('Testing scale connection...')
                      // Simulate scale test
                      await new Promise(resolve => setTimeout(resolve, 1500))
                      toast.success('Scale connection successful! Reading: 1.5 kg')
                    } catch (err) {
                      toast.error('Failed to connect to scale')
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
                >
                  <Scale size={16} />
                  Test Scale Connection
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  const renderCustomerDisplaySettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Monitor size={20} className="text-purple-400" />
          Customer Display Configuration
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Display Type</label>
            <select 
              value={settings.customer_display_type || 'none'}
              onChange={(e) => updateSetting('customer_display_type', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="none">No Customer Display</option>
              <option value="pole">Pole Display (Serial/USB)</option>
              <option value="lcd">LCD Display</option>
              <option value="web">Web-based Display (Tablet/Monitor)</option>
              <option value="dual_monitor">Dual Monitor Setup</option>
            </select>
          </div>

          {settings.customer_display_type !== 'none' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Connection Port/URL</label>
                <input
                  type="text"
                  value={settings.customer_display_port || ''}
                  onChange={(e) => updateSetting('customer_display_port', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                  placeholder="e.g., COM4, /dev/ttyUSB1, http://192.168.1.101:8080"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Display Language</label>
                <select 
                  value={settings.customer_display_language || 'en'}
                  onChange={(e) => updateSetting('customer_display_language', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="ar">Arabic</option>
                  <option value="zh">Chinese</option>
                  <option value="ja">Japanese</option>
                </select>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-gray-700">
                <div>
                  <div className="text-sm font-medium">Show Item Images</div>
                  <div className="text-xs text-gray-400">Display product images on customer screen</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={settings.customer_display_show_images === 'true'}
                    onChange={(e) => updateSetting('customer_display_show_images', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-700">
                <div>
                  <div className="text-sm font-medium">Show Running Total</div>
                  <div className="text-xs text-gray-400">Display live transaction total</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={settings.customer_display_show_total === 'true'}
                    onChange={(e) => updateSetting('customer_display_show_total', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Welcome Message</label>
                <input
                  type="text"
                  value={settings.customer_display_welcome || 'Welcome!'}
                  onChange={(e) => updateSetting('customer_display_welcome', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Thank You Message</label>
                <input
                  type="text"
                  value={settings.customer_display_thankyou || 'Thank you for your business!'}
                  onChange={(e) => updateSetting('customer_display_thankyou', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  const renderEmailSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Mail size={20} className="text-blue-400" />
          SMTP Configuration
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">SMTP Host *</label>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="smtp.gmail.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">SMTP Port *</label>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="587"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Encryption</label>
              <select 
                value={smtpEncryption}
                onChange={(e) => setSmtpEncryption(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              >
                <option value="tls">TLS (587)</option>
                <option value="ssl">SSL (465)</option>
                <option value="none">None</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">SMTP Username *</label>
              <input
                type="text"
                value={smtpUsername}
                onChange={(e) => setSmtpUsername(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="your-email@example.com"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">SMTP Password *</label>
              <div className="relative">
                <input
                  type={showSmtpPassword ? 'text' : 'password'}
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">From Email Address</label>
              <input
                type="email"
                value={settings.from_email || ''}
                onChange={(e) => updateSetting('from_email', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="noreply@yourcompany.com"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">From Name</label>
              <input
                type="text"
                value={settings.from_name || ''}
                onChange={(e) => updateSetting('from_name', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                placeholder="Your Company Name"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-700">
            <button
              onClick={testSMTPConnection}
              disabled={testStatus === 'testing'}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {testStatus === 'testing' ? (
                <>
                  <RefreshCw className="animate-spin" size={16} />
                  Testing Connection...
                </>
              ) : testStatus === 'success' ? (
                <>
                  <CheckCircle size={16} />
                  Connection Successful
                </>
              ) : testStatus === 'error' ? (
                <>
                  <AlertCircle size={16} />
                  Connection Failed
                </>
              ) : (
                <>
                  <Mail size={16} />
                  Test SMTP Connection
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Email Templates</h3>
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Trigger</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Default</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {emailTemplates.map(template => (
                <tr key={template.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-sm text-white">{template.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{template.template_type || 'General'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{template.trigger_event || 'Manual'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      template.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {template.is_default && (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button className="text-blue-400 hover:text-blue-300 text-sm">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderPrintSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Printer size={20} className="text-purple-400" />
          Printer Configurations
        </h3>
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Connection</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Paper Size</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Default</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {printers.map(printer => (
                <tr key={printer.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-sm text-white">{printer.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{printer.printer_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {printer.connection_type}
                    {printer.ip_address && ` (${printer.ip_address}:${printer.port})`}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{printer.paper_width ? `${printer.paper_width}mm` : '80mm'}</td>
                  <td className="px-4 py-3 text-center">
                    {printer.is_default && (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      printer.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {printer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderUsersSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users size={20} className="text-blue-400" />
          User Management
        </h3>
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="text-center py-8 text-gray-400">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-white font-medium mb-2">Manage Users in Management</p>
            <p className="text-sm mb-6">Create users, assign roles, and manage permissions from the Management module.</p>
            <button
              onClick={() => navigate('/app/management?tab=users')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Users size={16} />
              Go to User Management
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderNotificationsSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bell size={20} className="text-yellow-400" />
          Notification Preferences
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Low Stock Alerts</div>
              <div className="text-xs text-gray-400">Notify when products reach minimum stock level</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.notify_low_stock === 'true'}
                onChange={(e) => updateSetting('notify_low_stock', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Daily Sales Report</div>
              <div className="text-xs text-gray-400">Email daily sales summary at end of business day</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.notify_daily_report === 'true'}
                onChange={(e) => updateSetting('notify_daily_report', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">New Order Notifications</div>
              <div className="text-xs text-gray-400">Sound alert for new orders</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.notify_new_order === 'true'}
                onChange={(e) => updateSetting('notify_new_order', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">System Updates</div>
              <div className="text-xs text-gray-400">Notify about software updates and maintenance</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.notify_system_updates === 'true'}
                onChange={(e) => updateSetting('notify_system_updates', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="pt-4 border-t border-gray-700">
            <label className="block text-sm font-medium mb-2">Notification Email Recipients</label>
            <input
              type="text"
              value={settings.notification_emails || ''}
              onChange={(e) => updateSetting('notification_emails', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
              placeholder="manager@example.com, owner@example.com"
            />
            <div className="text-xs text-gray-400 mt-1">
              Separate multiple emails with commas
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Alert Sound</label>
            <select 
              value={settings.alert_sound || 'default'}
              onChange={(e) => updateSetting('alert_sound', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="none">No Sound</option>
              <option value="default">Default Beep</option>
              <option value="chime">Chime</option>
              <option value="bell">Bell</option>
              <option value="ding">Ding</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )

  const renderSecuritySettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield size={20} className="text-red-400" />
          Security & Access Control
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Password Policy</label>
            <select 
              value={settings.password_policy || 'medium'}
              onChange={(e) => updateSetting('password_policy', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="low">Low (6+ characters)</option>
              <option value="medium">Medium (8+ characters, mixed case)</option>
              <option value="high">High (10+ chars, mixed case, numbers, symbols)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Session Timeout (minutes)</label>
            <input
              type="number"
              min="5"
              max="480"
              value={settings.session_timeout || '30'}
              onChange={(e) => updateSetting('session_timeout', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Max Failed Login Attempts</label>
            <input
              type="number"
              min="3"
              max="10"
              value={settings.max_login_attempts || '5'}
              onChange={(e) => updateSetting('max_login_attempts', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-center justify-between py-3 border-t border-gray-700">
            <div>
              <div className="text-sm font-medium">Require Two-Factor Authentication</div>
              <div className="text-xs text-gray-400">Enable 2FA for all admin users</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.require_2fa === 'true'}
                onChange={(e) => updateSetting('require_2fa', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Enable Audit Logging</div>
              <div className="text-xs text-gray-400">Track all user actions and system changes</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.enable_audit_log === 'true'}
                onChange={(e) => updateSetting('enable_audit_log', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">IP Whitelisting</div>
              <div className="text-xs text-gray-400">Restrict access to specific IP addresses</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.enable_ip_whitelist === 'true'}
                onChange={(e) => updateSetting('enable_ip_whitelist', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {settings.enable_ip_whitelist === 'true' && (
            <div className="pt-4 border-t border-gray-700">
              <label className="block text-sm font-medium mb-2">Allowed IP Addresses</label>
              <textarea
                value={settings.allowed_ips || ''}
                onChange={(e) => updateSetting('allowed_ips', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm"
                rows={3}
                placeholder="192.168.1.1&#10;10.0.0.0/24&#10;One IP or subnet per line"
              />
            </div>
          )}

          <div className="pt-4 border-t border-gray-700">
            <button
              onClick={() => {
                if (confirm('Are you sure you want to force password reset for all users?')) {
                  toast.success('Password reset initiated for all users')
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Force Password Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderIntegrationsSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Layers size={20} className="text-purple-400" />
          System Integrations
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Accounting Integration</div>
              <div className="text-xs text-gray-400">Sync with QuickBooks/Xero</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.accounting_integration === 'true'}
                onChange={(e) => updateSetting('accounting_integration', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Payment Gateway Integration</div>
              <div className="text-xs text-gray-400">Connect to Stripe/Square</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.payment_gateway_integration === 'true'}
                onChange={(e) => updateSetting('payment_gateway_integration', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-700">
            <div>
              <div className="text-sm font-medium">Inventory Management</div>
              <div className="text-xs text-gray-400">Sync with external inventory systems</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.inventory_integration === 'true'}
                onChange={(e) => updateSetting('inventory_integration', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">CRM Integration</div>
              <div className="text-xs text-gray-400">Connect to CRM systems</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox"
                checked={settings.crm_integration === 'true'}
                onChange={(e) => updateSetting('crm_integration', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="pt-4 border-t border-gray-700">
            <h4 className="text-sm font-semibold mb-3">API Configuration</h4>
            <div className="bg-gray-900 rounded p-4 text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">API Status:</span>
                <span className="text-green-400">Active</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">API Version:</span>
                <span className="text-white">v1.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Rate Limit:</span>
                <span className="text-white">1000 requests/hour</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderAboutSettings = () => (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Info size={20} className="text-blue-400" />
          About Elysium POS
        </h3>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="text-center py-6">
            <div className="text-4xl font-bold text-blue-400 mb-2">Elysium POS</div>
            <div className="text-gray-400 mb-4">Version 1.0.0</div>
            <div className="text-sm text-gray-500">
              Enterprise-grade Point of Sale System
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Build Date:</span>
              <span className="text-white">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Database Version:</span>
              <span className="text-white">PostgreSQL 15</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">API Version:</span>
              <span className="text-white">v1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Schema Version:</span>
              <span className="text-white">168 Tables</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">License Type:</span>
              <span className="text-white">Commercial</span>
            </div>
          </div>

          {company && (
            <div className="border-t border-gray-700 pt-4">
              <div className="text-sm font-medium mb-2">Licensed To:</div>
              <div className="text-sm text-gray-400">{company.name}</div>
              {company.email && <div className="text-xs text-gray-500">{company.email}</div>}
            </div>
          )}

          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-semibold mb-3">System Resources</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Active Stores:</span>
                <span className="text-white">{stores.filter(s => s.is_active).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Active Registers:</span>
                <span className="text-white">{cashRegisters.filter(cr => cr.is_active).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Tax Rates:</span>
                <span className="text-white">{taxRates.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Payment Types:</span>
                <span className="text-white">{paymentTypes.length}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <button
              onClick={async () => {
                try {
                  const response = await apiClient.get('/api/system/health')
                  toast.success('System health check completed')
                  console.log('System health:', response.data)
                } catch (err) {
                  toast.error('Health check failed')
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              System Health Check
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <RefreshCw className="animate-spin text-blue-400" size={32} />
        </div>
      )
    }

    switch (activeTab) {
      case 'general':
        return renderGeneralSettings()
      case 'company':
        return renderCompanySettings()
      case 'stores':
        return renderStoresSettings()
      case 'cash-registers':
        return renderCashRegistersSettings()
      case 'terminal-config':
        return renderTerminalConfigSettings()
      case 'taxes':
        return renderTaxSettings()
      case 'payment-types':
        return renderPaymentTypesSettings()
      case 'order-payment':
        return renderOrderPaymentSettings()
      case 'products':
        return renderProductSettings()
      case 'documents':
        return renderDocumentsSettings()
      case 'weighing-scale':
        return renderWeighingScaleSettings()
      case 'customer-display':
        return renderCustomerDisplaySettings()
      case 'email':
        return renderEmailSettings()
      case 'print':
        return renderPrintSettings()
      case 'users':
        return renderUsersSettings()
      case 'notifications':
        return renderNotificationsSettings()
      case 'security':
        return renderSecuritySettings()
      case 'integrations':
        return renderIntegrationsSettings()
      case 'about':
        return renderAboutSettings()
      default:
        return (
          <div className="text-center py-12 text-gray-400">
            Settings for {tabs.find(t => t.key === activeTab)?.label} coming soon
          </div>
        )
    }
  }

  const hasChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings) ||
           JSON.stringify(company) !== JSON.stringify(originalCompany) ||
           smtpHost !== (originalSettings.smtp_host || '') ||
           smtpPort !== (originalSettings.smtp_port || '587') ||
           smtpUsername !== (originalSettings.smtp_username || '') ||
           smtpPassword !== (originalSettings.smtp_password || '') ||
           smtpEncryption !== (originalSettings.smtp_encryption || 'tls')
  }, [settings, originalSettings, company, originalCompany, smtpHost, smtpPort, smtpUsername, smtpPassword, smtpEncryption])

  return (
    <ErrorBoundary>
      <div className="h-screen flex flex-col bg-gray-900 text-white overflow-hidden">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/app')} 
              className="text-gray-400 hover:text-white transition-colors"
              title="Back to Dashboard"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <SettingsIcon size={18} />
              System Settings
            </h2>
          </div>
          <div className="flex items-center gap-4">
            {hasChanges && (
              <span className="text-xs text-yellow-400 flex items-center gap-1 animate-pulse">
                <Zap size={12} />
                Unsaved Changes
              </span>
            )}
            <span className="text-sm text-gray-400 hidden md:inline">{user?.email}</span>
            <button 
              onClick={handleMaximize} 
              className="p-2 hover:bg-gray-700 rounded transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              <Maximize size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-56 bg-gray-800 border-r border-gray-700 overflow-y-auto flex-shrink-0">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors border-l-2 flex items-center gap-3 ${
                    activeTab === tab.key 
                      ? 'border-blue-500 bg-gray-900 text-blue-400' 
                      : 'border-transparent text-gray-300'
                  }`}
                  title={tab.label}
                >
                  <Icon size={16} />
                  <span className="text-sm truncate">{tab.label}</span>
                </button>
              )
            })}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-800 px-6 py-3 border-t border-gray-700 flex justify-between items-center flex-shrink-0">
          <div className="text-xs text-gray-500">
            {allSettings.length} settings loaded • {stores.length} stores • {cashRegisters.length} cash registers
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchAllData()}
              disabled={loading}
              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              title="Refresh all data"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleReset}
              disabled={!hasChanges || saving}
              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Discard changes"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              title="Save all changes"
            >
              {saving ? (
                <>
                  <RefreshCw className="animate-spin" size={16} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
}